/* eslint-disable react-hooks/exhaustive-deps */
import React,{useEffect,useState} from 'react';
import {useNavigate,useParams,useSearchParams} from 'react-router-dom';
import Layout from '../components/layout/Layout';
import {Input,Select,Textarea} from '../components/common/FormField';
import api from '../services/api';
import {fmtDateTime} from '../utils/helpers';
import {printMortuaryTag} from '../utils/mortuaryLabels';
import {prettyMortuary} from '../utils/mortuaryLabels';

const unwrap=(r:any)=>r?.data?.data??r?.data??r;
const TABS=[
  ['overview','Overview'],
  ['identity','Identity'],
  ['storage','Storage'],
  ['family','Family'],
  ['clinical','Autopsy'],
  ['prepare','Prepare'],
  ['release','Release'],
  ['audit','Audit'],
] as const;
const STAGE_TAB: Record<string,typeof TABS[number][0]>={
  REGISTER:'overview',IDENTIFY:'identity',STORE:'storage',MLC:'clinical',
  VIEWING:'family',AUTOPSY:'clinical',LAB:'clinical',RADIOLOGY:'clinical',
  FINALIZE:'clinical',EMBALM:'prepare',BILLING:'prepare',CHAPEL:'family',
  TRANSPORT:'prepare',RELEASE:'release',DISPOSITION:'release',CLOSURE:'release',ARCHIVE:'release',
};
const STAGE_CLS: Record<string,string>={
  DONE:'bg-emerald-50 text-emerald-800 border-emerald-200',
  IN_PROGRESS:'bg-sky-50 text-sky-800 border-sky-200',
  PENDING:'bg-slate-50 text-slate-600 border-slate-200',
  BLOCKED:'bg-red-50 text-red-700 border-red-200',
  OPTIONAL:'bg-amber-50 text-amber-800 border-amber-200',
  SKIPPED:'bg-white text-slate-400 border-slate-200',
};

function Field({label,required,wide,children}:{label:string;required?:boolean;wide?:boolean;children:React.ReactNode}){
  return(
    <div className={wide?'md:col-span-2':''}>
      <label className="block text-[11px] font-semibold tracking-wide text-slate-500 mb-1 uppercase">
        {label}{required&&<span className="text-red-500"> *</span>}
      </label>
      {children}
    </div>
  );
}
function Pill({text,cls}:{text:string;cls:string}){
  return text?<span className={`inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full ${cls}`}>{text}</span>:null;
}
function Fact({label,value}:{label:string;value?:any}){
  return(
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">{label}</p>
      <p className="text-sm text-slate-800 truncate">{value||'—'}</p>
    </div>
  );
}

const TAB_IDS=TABS.map(([k])=>k);
const OPEN=new Set(['PENDING','IN_PROGRESS','BLOCKED']);

export default function MortuaryBodyPage(){
  const {id,token}=useParams();
  const nav=useNavigate();
  const [params]=useSearchParams();
  const tabFromUrl=params.get('tab');
  const [body,setBody]=useState<any>(null);
  const [workflow,setWorkflow]=useState<any>(null);
  const [lookups,setLookups]=useState<any>({});
  const [chambers,setChambers]=useState<any[]>([]);
  const [msg,setMsg]=useState('');
  const [saving,setSaving]=useState('');
  const [tab,setTab]=useState<(typeof TABS)[number][0]>(()=>
    (TAB_IDS as readonly string[]).includes(tabFromUrl||'')?tabFromUrl as (typeof TABS)[number][0]:'overview'
  );
  const [idForm,setIdForm]=useState<any>({identificationStatus:'IDENTIFIED'});
  const [storeForm,setStoreForm]=useState<any>({chamberId:'',reason:'Storage assignment'});
  const [pmForm,setPmForm]=useState<any>({status:'Requested',required:true});
  const [belForm,setBelForm]=useState<any>({itemDescription:'',quantity:1});
  const [relForm,setRelForm]=useState<any>({releaseType:'FAMILY',confirmed:false});
  const [trForm,setTrForm]=useState<any>({transferType:'CHAMBER',reason:''});
  const [ucForm,setUcForm]=useState<any>({});
  const [nokForm,setNokForm]=useState<any>({isPrimary:true});
  const [flagForm,setFlagForm]=useState<any>({});
  const [holdReason,setHoldReason]=useState('');
  const [embForm,setEmbForm]=useState<any>({status:'REQUESTED',consentObtained:false});
  const [viewForm,setViewForm]=useState<any>({visitorCount:1});
  const [civilNo,setCivilNo]=useState('');
  const [portalKind,setPortalKind]=useState('FAMILY');
  const [portalLink,setPortalLink]=useState('');
  const [scanTag,setScanTag]=useState('');
  const [gate,setGate]=useState<any>(null);

  const load=async()=>{
    try{
      const r=token?await api.scanMortuaryToken(token):await api.getMortuaryBody(id as string);
      const b=unwrap(r);
      setBody(b);
      if(b.id){
        const [g,w]=await Promise.all([
          api.getMortuaryReleaseGate(b.id).then(unwrap).catch(()=>null),
          api.getMortuaryWorkflow(b.id).then(unwrap).catch(()=>null),
        ]);
        setGate(g);
        setWorkflow(w);
      }
      if(b.postMortem) setPmForm({...b.postMortem,status:b.postMortem.status,required:b.postMortem.required});
      setIdForm((f:any)=>({
        ...f,
        identificationStatus:b.identification_status||f.identificationStatus||'IDENTIFIED',
        identifiedName:f.identifiedName||b.full_name||'',
        idNumber:f.idNumber||b.national_id||'',
      }));
      setFlagForm({
        medicoLegal:!!b.medico_legal,infectious:!!b.infectious,isolationPrecautions:b.isolation_precautions||'',
        religion:b.religion||'',ritesNotes:b.rites_notes||'',bodyWeightKg:b.body_weight_kg||'',
        bodyHeightCm:b.body_height_cm||'',bloodGroup:b.blood_group||'',viewingAllowed:b.viewing_allowed!==false,
      });
      setHoldReason(b.legal_hold_reason||'');
      setCivilNo(b.civil_registry_number||'');
      if(b.embalming_status) setEmbForm((f:any)=>({...f,status:b.embalming_status}));
    }catch(e:any){setMsg(e.response?.data?.message||'Record not found');}
  };
  useEffect(()=>{
    load();
    api.getMortuaryLookups().then(r=>setLookups(unwrap(r)||{}));
    api.getMortuaryChambers().then(r=>setChambers(unwrap(r)||[]));
    const saved=sessionStorage.getItem(`mortuary-tab:${id||token}`);
    if(!tabFromUrl && (TAB_IDS as readonly string[]).includes(saved||'')) setTab(saved as (typeof TABS)[number][0]);
  },[id,token]);

  const run=async(key:string,fn:()=>Promise<any>)=>{
    setSaving(key);
    try{await fn();setMsg('✅ Saved');await load();}
    catch(e:any){setMsg('❌ '+(e.response?.data?.message||e.response?.data?.data?.message||'Action failed'));}
    finally{setSaving('');}
  };

  const printHandover=()=>{
    const r=body?.releases?.[0];
    const w=window.open('','_blank');
    if(!w) return;
    w.document.write(`<html><head><title>Body Release ${body.body_number}</title>
      <style>body{font-family:Arial;padding:24px;color:#0f172a}h1{color:#02357A}table{width:100%;border-collapse:collapse}td{padding:6px;border-bottom:1px solid #e2e8f0}</style></head>
      <body><h1>Body Release / Handover Form</h1>
      <p><b>${body.body_number}</b> — ${body.full_name}</p>
      <table>
        <tr><td>Recipient</td><td>${r?.recipient_name||relForm.recipientName||''}</td></tr>
        <tr><td>Relationship</td><td>${r?.recipient_relationship||relForm.recipientRelationship||''}</td></tr>
        <tr><td>ID</td><td>${r?.recipient_id_number||relForm.recipientIdNumber||''}</td></tr>
        <tr><td>Release type</td><td>${r?.release_type||relForm.releaseType||''}</td></tr>
        <tr><td>Released at</td><td>${fmtDateTime(r?.released_at||new Date().toISOString())}</td></tr>
        <tr><td>Destination</td><td>${r?.destination||relForm.destination||''}</td></tr>
        <tr><td>Witness</td><td>${r?.witness||relForm.witness||''}</td></tr>
      </table>
      <p style="margin-top:48px">Recipient signature: ______________________</p>
      <script>window.print()<\/script></body></html>`);
    w.document.close();
  };

  if(!body) return <Layout><div className="card p-8 text-center text-slate-400">{msg||'Loading body workbench…'}</div></Layout>;
  const opts=(t:string)=>lookups[t]||[];
  const released=body.status==='RELEASED'||body.status==='DISPOSED'||body.status==='FINAL_DISPOSITION_COMPLETED';
  const current=workflow?.current;
  const nextAction=workflow?.nextAction;
  const goTab=(k:(typeof TABS)[number][0])=>{
    setTab(k);
    sessionStorage.setItem(`mortuary-tab:${body.id}`,k);
    nav(`/mortuary/bodies/${body.id}?tab=${k}`,{replace:true});
  };
  const openStage=(key?:string)=>{
    if(!key) return;
    if(key==='CHAPEL'){goTab('family');return;}
    if(key==='TRANSPORT'){goTab('prepare');return;}
    if(key==='DISPOSITION'||key==='CLOSURE'||key==='ARCHIVE'){goTab('release');return;}
    goTab(STAGE_TAB[key]||'overview');
  };
  const goNext=()=>{
    const stages=workflow?.stages||[];
    if(tab==='overview'){
      const first=stages.find((s:any)=>OPEN.has(s.state)&&s.key!=='REGISTER');
      openStage(first?.key||current||'IDENTIFY');
      return;
    }
    const here=stages.findIndex((s:any)=>STAGE_TAB[s.key]===tab);
    const nxt=stages.slice(Math.max(here,0)+1).find((s:any)=>!['DONE','SKIPPED'].includes(s.state));
    if(nxt) openStage(nxt.key);
    else if(current&&STAGE_TAB[current]!==tab) openStage(current);
  };

  return(
    <Layout>
      <div className="space-y-4">
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm">
          <div className="bg-gradient-to-r from-[#02357A] to-[#015CA6] px-5 py-4 text-white">
            <div className="flex flex-wrap justify-between gap-3">
              <div>
                <button className="text-[11px] text-white/80 hover:text-white" onClick={()=>nav('/mortuary/records?queue=in-process')}>← Back to register</button>
                <p className="text-[11px] uppercase tracking-wide text-white/70 mt-1">Daily workbench</p>
                <h1 className="text-xl font-bold mt-0.5">{body.full_name||'Unknown deceased'}</h1>
                <p className="text-xs text-white/80">{body.case_number||'Case pending'} · {body.body_number} · {prettyMortuary(body.lifecycle_status||body.status)}</p>
                <p className="text-[11px] text-white/70 mt-1">Use this screen for day-to-day work: identify, assign a chamber, family, and release.</p>
              </div>
              <div className="flex flex-wrap gap-2 items-start">
                <button className="bg-white text-[#02357A] text-sm font-semibold px-3 py-1.5 rounded-lg" onClick={()=>nav(`/mortuary/bodies/${body.id}/case`)}>Full case file</button>
                <button className="bg-white/15 text-white text-sm font-semibold px-3 py-1.5 rounded-lg border border-white/30" onClick={()=>nav(`/mortuary/bodies/${body.id}/autopsy`)}>Autopsy</button>
                <button className="bg-white/15 text-white text-sm font-semibold px-3 py-1.5 rounded-lg border border-white/30" onClick={()=>nav(`/mortuary/chapel?bodyId=${body.id}`)}>Chapel</button>
                <button className="bg-white/15 text-white text-sm font-semibold px-3 py-1.5 rounded-lg border border-white/30" onClick={()=>nav(`/mortuary/transport?bodyId=${body.id}`)}>Transport</button>
                <button className="bg-white/15 text-white text-sm font-semibold px-3 py-1.5 rounded-lg border border-white/30" onClick={()=>nav(`/mortuary/cemetery?bodyId=${body.id}`)}>Disposition</button>
                <button className="bg-white/15 text-white text-sm font-semibold px-3 py-1.5 rounded-lg border border-white/30" onClick={()=>printMortuaryTag({bodyNumber:body.body_number,scanToken:body.scan_token,chamber:body.chamber?.chamber_number})}>Print tag</button>
                <button className="bg-white/15 text-white text-sm font-semibold px-3 py-1.5 rounded-lg border border-white/30" onClick={printHandover}>Handover</button>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-3">
              <Pill text={prettyMortuary(body.identification_status)} cls="bg-white/15 text-white"/>
              <Pill text={body.chamber?.chamber_number?`Chamber ${body.chamber.chamber_number}`:'No chamber'} cls={body.chamber?'bg-emerald-300/20 text-white':'bg-amber-300/25 text-amber-50'}/>
              {body.legal_hold&&<Pill text="LEGAL HOLD" cls="bg-red-500 text-white"/>}
              {body.medico_legal&&<Pill text="MLC" cls="bg-amber-400 text-[#02357A]"/>}
              {body.infectious&&<Pill text="INFECTIOUS" cls="bg-orange-400 text-white"/>}
              {body.post_mortem_required&&<Pill text="AUTOPSY REQUIRED" cls="bg-white/15 text-white"/>}
              {body.patient_id&&<Pill text="PATIENT LINKED" cls="bg-teal-400/30 text-white"/>}
            </div>
          </div>

          {workflow?.stages&&(
            <div className="px-4 py-3 border-b bg-slate-50 overflow-x-auto">
              <div className="flex gap-1 min-w-[920px]">
                {workflow.stages.map((s:any)=>(
                  <button key={s.key} onClick={()=>openStage(s.key)}
                    className={`flex-1 min-w-[52px] border rounded-lg px-1 py-1.5 text-center ${STAGE_CLS[s.state]||STAGE_CLS.PENDING} ${current===s.key?'ring-2 ring-[#02357A]':''}`}>
                    <div className="text-[9px] font-bold">{s.n}</div>
                    <div className="text-[10px] leading-tight font-medium">{s.label.split(/[/(]/)[0].trim()}</div>
                  </button>
                ))}
              </div>
              {nextAction&&(
                <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-sm">
                  <p className="text-[#02357A] font-medium">Next: {nextAction}</p>
                  <button className="btn-primary text-xs py-1" onClick={goNext}>Go to next step</button>
                </div>
              )}
            </div>
          )}
        </div>

        {msg&&<div className={`text-sm rounded-xl px-4 py-2 border ${msg.startsWith('❌')?'bg-red-50 border-red-200 text-red-800':'bg-emerald-50 border-emerald-200 text-emerald-800'}`}>{msg}</div>}

        <div className="flex flex-wrap gap-1.5">
          {TABS.map(([k,l])=>(
            <button key={k} onClick={()=>goTab(k)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${tab===k?'bg-[#02357A] text-white border-[#015CA6]':'bg-white border-slate-200 text-slate-600 hover:border-[#015CA6]'}`}>
              {l}
            </button>
          ))}
        </div>

        {tab==='overview'&&(
          <div className="grid lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="rounded-xl border border-sky-100 bg-sky-50 p-3">
                <p className="text-xs font-bold text-[#02357A]">This screen — daily work</p>
                <p className="text-xs text-slate-600 mt-0.5">Identify the body, assign a chamber, add family, prepare and release. Use <b>Go to next step</b> to move through the work.</p>
              </div>
              <button type="button" className="rounded-xl border border-slate-200 bg-white p-3 text-left hover:border-[#015CA6]" onClick={()=>nav(`/mortuary/bodies/${body.id}/case`)}>
                <p className="text-xs font-bold text-slate-700">Full case file</p>
                <p className="text-xs text-slate-500 mt-0.5">Open autopsy, specimens, evidence, billing and the complete timeline for this case.</p>
              </button>
            </div>
            <div className="card p-5 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-[#02357A]">Deceased</h3>
                <span className="text-[11px] text-slate-400">Received {fmtDateTime(body.received_at)}</span>
              </div>
              <div className="grid sm:grid-cols-3 gap-x-4 gap-y-3">
                <Fact label="Body number" value={body.body_number}/>
                <Fact label="Case number" value={body.case_number}/>
                <Fact label="Status" value={prettyMortuary(body.lifecycle_status||body.status)}/>
                <Fact label="Gender / age" value={[body.gender,body.age_years&&`${body.age_years}y`].filter(Boolean).join(' · ')}/>
                <Fact label="National ID" value={body.national_id}/>
                <Fact label="Patient" value={body.patient_uhid||(body.patient_id?'Linked':'Not linked')}/>
                <Fact label="Source" value={body.source}/>
                <Fact label="Death type" value={body.death_type}/>
                <Fact label="Date of death" value={fmtDateTime(body.date_of_death)}/>
                <Fact label="Place of death" value={body.place_of_death}/>
                <Fact label="Cause" value={body.cause_of_death}/>
                <Fact label="Certificate" value={body.death_certificate_number}/>
                <Fact label="Police / MLC" value={body.police_reference_number|| (body.medico_legal?'MLC — no reference':'—')}/>
                <Fact label="Brought by" value={body.brought_by?`${body.brought_by} (${body.brought_by_relationship||'—'})`:'—'}/>
                <Fact label="Condition" value={body.body_condition}/>
                <Fact label="Religion" value={body.religion}/>
                <Fact label="Weight / height" value={[body.body_weight_kg&&`${body.body_weight_kg} kg`,body.body_height_cm&&`${body.body_height_cm} cm`].filter(Boolean).join(' · ')}/>
                <Fact label="Blood group" value={body.blood_group}/>
                <Fact label="Chamber" value={body.chamber?.chamber_number||'Unassigned'}/>
                <Fact label="Scan token" value={body.scan_token}/>
              </div>
              {(body.visible_injuries||body.personal_belongings_note||body.notes)&&(
                <div className="text-sm text-slate-600 space-y-1 border-t pt-3">
                  {body.visible_injuries&&<p><span className="font-medium text-slate-500">Injuries: </span>{body.visible_injuries}</p>}
                  {body.personal_belongings_note&&<p><span className="font-medium text-slate-500">Belongings: </span>{body.personal_belongings_note}</p>}
                  {body.notes&&<p><span className="font-medium text-slate-500">Notes: </span>{body.notes}</p>}
                </div>
              )}
            </div>
            </div>
            <div className="space-y-4">
              <div className="card p-4 space-y-3">
                <h3 className="font-semibold text-[#02357A]">RFID / QR scan</h3>
                <p className="text-xs text-slate-500">Scan the body tag or type the token to record a location event.</p>
                <Input placeholder="Scan or paste token" value={scanTag} onChange={e=>setScanTag(e.target.value)}/>
                <button disabled={!scanTag||saving==='sc'} className="btn-primary text-sm w-full" onClick={()=>run('sc',()=>api.recordMortuaryScan({bodyId:body.id,tag:scanTag,eventType:'SCAN',location:body.chamber?.chamber_number}))}>Record scan</button>
              </div>
              <div className="card p-4 space-y-2">
                <h3 className="font-semibold text-[#02357A]">Release readiness</h3>
                {(gate?.items||[]).slice(0,8).map((i:any)=>(
                  <div key={i.key} className="flex gap-2 text-xs">
                    <span className={`mt-0.5 h-4 w-4 rounded-full text-center text-[10px] font-bold ${i.ok?'bg-emerald-100 text-emerald-700':'bg-red-100 text-red-700'}`}>{i.ok?'✓':'!'}</span>
                    <span className={i.ok?'text-slate-600':'text-red-700'}>{i.label}{i.required?' *':''}</span>
                  </div>
                ))}
                <button className="btn-secondary text-sm w-full mt-2" onClick={()=>goTab('release')}>Open release checklist</button>
              </div>
            </div>
          </div>
        )}

        {tab==='identity'&&(
          <div className="card p-5 space-y-4">
            <div>
              <h3 className="font-semibold text-[#02357A]">Identification</h3>
              <p className="text-xs text-slate-500">Record identified / unidentified, the identifier, relationship, and evidence.</p>
            </div>
            <div className="grid md:grid-cols-3 gap-3">
              <Field label="Identification status" required>
                <Select value={idForm.identificationStatus} onChange={e=>setIdForm({...idForm,identificationStatus:e.target.value})}>
                  {['IDENTIFIED','PARTIALLY_IDENTIFIED','UNIDENTIFIED','UNKNOWN'].map(s=><option key={s}>{s}</option>)}
                </Select>
              </Field>
              <Field label="Identified name"><Input value={idForm.identifiedName||''} onChange={e=>setIdForm({...idForm,identifiedName:e.target.value})}/></Field>
              <Field label="ID / passport number"><Input value={idForm.idNumber||''} onChange={e=>setIdForm({...idForm,idNumber:e.target.value})}/></Field>
              <Field label="Identifier name"><Input value={idForm.identifierName||''} onChange={e=>setIdForm({...idForm,identifierName:e.target.value})}/></Field>
              <Field label="Relationship"><Input value={idForm.identifierRelationship||''} onChange={e=>setIdForm({...idForm,identifierRelationship:e.target.value})}/></Field>
              <Field label="Identifier phone"><Input value={idForm.identifierPhone||''} onChange={e=>setIdForm({...idForm,identifierPhone:e.target.value})}/></Field>
              <Field label="Method">
                <Select value={idForm.identificationMethod||''} onChange={e=>setIdForm({...idForm,identificationMethod:e.target.value})}>
                  <option value="">Select method</option>
                  {opts('IDENTIFICATION_METHOD').map((x:any)=><option key={x.code} value={x.code}>{x.label}</option>)}
                </Select>
              </Field>
              <Field label="Notes"><Input value={idForm.notes||''} onChange={e=>setIdForm({...idForm,notes:e.target.value})}/></Field>
              <Field label="Evidence file / upload ref" wide><Input placeholder="Existing mortuary upload path" value={idForm.filePath||''} onChange={e=>setIdForm({...idForm,filePath:e.target.value,fileName:e.target.value})}/></Field>
            </div>
            <div className="flex justify-end">
              <button disabled={released||saving==='id'} className="btn-primary text-sm" onClick={()=>run('id',()=>api.identifyMortuaryBody(body.id,idForm))}>{saving==='id'?'Saving…':'Save identification'}</button>
            </div>
            {(body.identification||[]).length>0&&(
              <div className="border-t pt-3 text-xs text-slate-600 space-y-1">
                {(body.identification||[]).map((x:any)=>(
                  <p key={x.id}>{fmtDateTime(x.identified_at)} · {x.identification_status} · {x.identified_name||'—'} · {x.identifier_name||'no identifier'}</p>
                ))}
              </div>
            )}
          </div>
        )}

        {tab==='storage'&&(
          <div className="grid lg:grid-cols-2 gap-4">
            <div className="card p-5 space-y-3">
              <h3 className="font-semibold text-[#02357A]">Chamber assignment</h3>
              <p className="text-xs text-slate-500">Assign or move the body. Occupancy is enforced on the server.</p>
              <Field label="Chamber" required>
                <Select value={storeForm.chamberId} onChange={e=>setStoreForm({...storeForm,chamberId:e.target.value})}>
                  <option value="">Select chamber</option>
                  {chambers.map((c:any)=><option key={c.id} value={c.id}>{c.chamber_number} {c.rack_shelf_slot||''} · {c.status} ({c.current_occupancy}/{c.capacity})</option>)}
                </Select>
              </Field>
              <Field label="Reason"><Input value={storeForm.reason||''} onChange={e=>setStoreForm({...storeForm,reason:e.target.value})}/></Field>
              <div className="flex gap-2">
                <button disabled={released||saving==='st'} className="btn-primary text-sm" onClick={()=>run('st',()=>api.assignMortuaryStorage(body.id,storeForm))}>Assign storage</button>
                <button disabled={released||saving==='mv'} className="btn-secondary text-sm" onClick={()=>run('mv',()=>api.moveMortuaryBody(body.id,{toChamberId:storeForm.chamberId,reason:storeForm.reason}))}>Move</button>
              </div>
            </div>
            <div className="card p-5">
              <h3 className="font-semibold text-[#02357A] mb-2">Movement history</h3>
              <div className="text-sm space-y-2 max-h-72 overflow-auto">
                {(body.movements||[]).length?(body.movements||[]).map((m:any)=>(
                  <div key={m.id} className="border rounded-lg px-3 py-2 bg-slate-50">
                    <p className="font-medium">{m.from_label||'Intake'} → {m.to_label||m.to_chamber||'—'}</p>
                    <p className="text-xs text-slate-500">{fmtDateTime(m.moved_at)} · {m.reason||'—'}</p>
                  </div>
                )):<p className="text-slate-400 text-sm">No movements yet. Assign a chamber to start storage tracking.</p>}
              </div>
            </div>
          </div>
        )}

        {tab==='family'&&(
          <div className="grid lg:grid-cols-2 gap-4">
            <div className="card p-5 space-y-3">
              <h3 className="font-semibold text-[#02357A]">Next of kin</h3>
              <div className="grid md:grid-cols-2 gap-3">
                <Field label="Full name" required wide><Input value={nokForm.fullName||''} onChange={e=>setNokForm({...nokForm,fullName:e.target.value})}/></Field>
                <Field label="Relationship"><Input value={nokForm.relationship||''} onChange={e=>setNokForm({...nokForm,relationship:e.target.value})}/></Field>
                <Field label="Phone"><Input value={nokForm.phone||''} onChange={e=>setNokForm({...nokForm,phone:e.target.value})}/></Field>
                <Field label="National ID"><Input value={nokForm.nationalId||''} onChange={e=>setNokForm({...nokForm,nationalId:e.target.value})}/></Field>
                <Field label="Address" wide><Input value={nokForm.address||''} onChange={e=>setNokForm({...nokForm,address:e.target.value})}/></Field>
              </div>
              <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={!!nokForm.isPrimary} onChange={e=>setNokForm({...nokForm,isPrimary:e.target.checked})}/> Primary contact</label>
              <button disabled={saving==='nk'} className="btn-primary text-sm" onClick={()=>run('nk',()=>api.addMortuaryNextOfKin(body.id,nokForm).then(()=>setNokForm({isPrimary:true})))}>Add next of kin</button>
              <div className="space-y-1 text-sm">
                {(body.nextOfKin||[]).map((n:any)=>(
                  <p key={n.id} className="border rounded-lg px-3 py-1.5 bg-slate-50">{n.full_name} · {n.relationship||'—'} · {n.phone||'—'} {n.is_primary?'· Primary':''}</p>
                ))}
                {!(body.nextOfKin||[]).length&&<p className="text-slate-400 text-sm">No next of kin recorded.</p>}
              </div>
            </div>
            <div className="card p-5 space-y-3">
              <h3 className="font-semibold text-[#02357A]">Viewing</h3>
              <div className="grid md:grid-cols-2 gap-3">
                <Field label="Visitor name" required wide><Input value={viewForm.visitorName||''} onChange={e=>setViewForm({...viewForm,visitorName:e.target.value})}/></Field>
                <Field label="Scheduled at"><Input type="datetime-local" value={viewForm.scheduledAt||''} onChange={e=>setViewForm({...viewForm,scheduledAt:e.target.value})}/></Field>
                <Field label="Relationship"><Input value={viewForm.visitorRelationship||''} onChange={e=>setViewForm({...viewForm,visitorRelationship:e.target.value})}/></Field>
                <Field label="Visitor count"><Input type="number" value={viewForm.visitorCount||1} onChange={e=>setViewForm({...viewForm,visitorCount:e.target.value})}/></Field>
              </div>
              <button disabled={released||saving==='vw'} className="btn-secondary text-sm" onClick={()=>run('vw',()=>api.addMortuaryViewing(body.id,viewForm))}>Record viewing</button>
              <div className="text-sm space-y-1">
                {(body.viewings||[]).map((v:any)=><p key={v.id}>{fmtDateTime(v.scheduled_at)} · {v.visitor_name} × {v.visitor_count||1}</p>)}
              </div>
              <div className="border-t pt-3 space-y-2">
                <h4 className="text-sm font-semibold">Family / funeral portal</h4>
                <div className="flex gap-2">
                  <Select value={portalKind} onChange={e=>setPortalKind(e.target.value)}><option>FAMILY</option><option>FUNERAL_DIRECTOR</option></Select>
                  <button disabled={saving==='pt'} className="btn-secondary text-sm" onClick={()=>run('pt',async()=>{
                    const r=unwrap(await api.inviteMortuaryPortal(body.id,{kind:portalKind}));
                    setPortalLink(`${window.location.origin}/mortuary-portal?token=${r.token}`);
                    return r;
                  })}>Issue portal link</button>
                </div>
                {portalLink&&<p className="text-xs break-all bg-slate-50 border rounded-lg px-2 py-1">Share: {portalLink}</p>}
                <button className="text-xs text-[#02357A] underline" onClick={()=>nav(`/mortuary/chapel?bodyId=${body.id}`)}>Open chapel / funeral board</button>
              </div>
            </div>
          </div>
        )}

        {tab==='clinical'&&(
          <div className="grid lg:grid-cols-2 gap-4">
            <div className="card p-5 space-y-3">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-semibold text-[#02357A]">Autopsy / postmortem</h3>
                  <p className="text-xs text-slate-500">Use the autopsy workbench for transcript, AI draft, specimens and sign-off.</p>
                </div>
                <button className="btn-primary text-xs" onClick={()=>nav(`/mortuary/bodies/${body.id}/autopsy`)}>Open workbench</button>
              </div>
              <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={!!pmForm.required} onChange={e=>setPmForm({...pmForm,required:e.target.checked})}/> Autopsy required</label>
              <div className="grid md:grid-cols-2 gap-3">
                <Field label="Status">
                  <Select value={pmForm.status||'Requested'} onChange={e=>setPmForm({...pmForm,status:e.target.value})}>
                    {['Not Required','Requested','Scheduled','In Progress','Completed','Report Pending','Report Completed'].map(s=><option key={s}>{s}</option>)}
                  </Select>
                </Field>
                <Field label="Requested by"><Input value={pmForm.requestedBy||''} onChange={e=>setPmForm({...pmForm,requestedBy:e.target.value})}/></Field>
                <Field label="Police case number"><Input value={pmForm.policeCaseNumber||''} onChange={e=>setPmForm({...pmForm,policeCaseNumber:e.target.value})}/></Field>
                <Field label="Pathologist"><Input value={pmForm.pathologistName||''} onChange={e=>setPmForm({...pmForm,pathologistName:e.target.value})}/></Field>
                <Field label="Findings / cause" wide><Textarea rows={3} value={pmForm.findings||''} onChange={e=>setPmForm({...pmForm,findings:e.target.value})}/></Field>
              </div>
              <button disabled={released||saving==='pm'} className="btn-primary text-sm" onClick={()=>run('pm',()=>api.saveMortuaryPostMortem(body.id,pmForm))}>Save post-mortem</button>
            </div>
            <div className="card p-5 space-y-3">
              <h3 className="font-semibold text-[#02357A]">Clinical / legal flags</h3>
              <div className="flex flex-wrap gap-4 text-sm">
                <label className="flex items-center gap-2"><input type="checkbox" checked={!!flagForm.medicoLegal} onChange={e=>setFlagForm({...flagForm,medicoLegal:e.target.checked})}/> Medico-legal</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={!!flagForm.infectious} onChange={e=>setFlagForm({...flagForm,infectious:e.target.checked})}/> Infectious</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={flagForm.viewingAllowed!==false} onChange={e=>setFlagForm({...flagForm,viewingAllowed:e.target.checked})}/> Viewing allowed</label>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <Field label="Isolation precautions"><Input value={flagForm.isolationPrecautions||''} onChange={e=>setFlagForm({...flagForm,isolationPrecautions:e.target.value})}/></Field>
                <Field label="Religion / rites"><Input value={flagForm.religion||''} onChange={e=>setFlagForm({...flagForm,religion:e.target.value})}/></Field>
                <Field label="Weight (kg)"><Input value={flagForm.bodyWeightKg||''} onChange={e=>setFlagForm({...flagForm,bodyWeightKg:e.target.value})}/></Field>
                <Field label="Height (cm)"><Input value={flagForm.bodyHeightCm||''} onChange={e=>setFlagForm({...flagForm,bodyHeightCm:e.target.value})}/></Field>
                <Field label="Blood group"><Input value={flagForm.bloodGroup||''} onChange={e=>setFlagForm({...flagForm,bloodGroup:e.target.value})}/></Field>
                <Field label="Rites notes"><Input value={flagForm.ritesNotes||''} onChange={e=>setFlagForm({...flagForm,ritesNotes:e.target.value})}/></Field>
              </div>
              <button disabled={released||saving==='fl'} className="btn-primary text-sm" onClick={()=>run('fl',()=>api.updateMortuaryFlags(body.id,flagForm))}>Save flags</button>
              <div className="border-t pt-3 space-y-2">
                <h4 className="text-sm font-semibold">Legal hold</h4>
                {body.legal_hold&&<p className="text-xs text-red-700">Hold is active. Release and archive are blocked until lifted or overridden.</p>}
                <Field label="Hold reason"><Input value={holdReason} onChange={e=>setHoldReason(e.target.value)}/></Field>
                <div className="flex gap-2">
                  <button disabled={released||saving==='hd'} className="btn-primary text-sm" onClick={()=>run('hd',()=>api.setMortuaryLegalHold(body.id,{on:true,reason:holdReason}))}>Place hold</button>
                  <button disabled={saving==='hl'} className="btn-secondary text-sm" onClick={()=>run('hl',()=>api.setMortuaryLegalHold(body.id,{on:false,reason:holdReason||'Cleared'}))}>Lift hold</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {tab==='prepare'&&(
          <div className="grid lg:grid-cols-2 gap-4">
            <div className="card p-5 space-y-3">
              <h3 className="font-semibold text-[#02357A]">Embalming / preparation</h3>
              <div className="grid md:grid-cols-2 gap-3">
                <Field label="Status">
                  <Select value={embForm.status} onChange={e=>setEmbForm({...embForm,status:e.target.value})}>
                    {['REQUESTED','IN_PROGRESS','COMPLETED','NOT_REQUIRED'].map(s=><option key={s}>{s}</option>)}
                  </Select>
                </Field>
                <Field label="Method"><Input value={embForm.method||''} onChange={e=>setEmbForm({...embForm,method:e.target.value})}/></Field>
                <Field label="Performer"><Input value={embForm.performerName||''} onChange={e=>setEmbForm({...embForm,performerName:e.target.value})}/></Field>
                <Field label="Consent given by"><Input value={embForm.consentBy||''} onChange={e=>setEmbForm({...embForm,consentBy:e.target.value})}/></Field>
                <Field label="Notes" wide><Textarea rows={2} value={embForm.notes||''} onChange={e=>setEmbForm({...embForm,notes:e.target.value})}/></Field>
              </div>
              <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={!!embForm.consentObtained} onChange={e=>setEmbForm({...embForm,consentObtained:e.target.checked})}/> Consent obtained</label>
              <button disabled={released||saving==='em'} className="btn-primary text-sm" onClick={()=>run('em',()=>api.saveMortuaryEmbalming(body.id,embForm))}>Save embalming</button>
            </div>
            <div className="card p-5 space-y-3">
              <h3 className="font-semibold text-[#02357A]">Belongings</h3>
              <div className="grid md:grid-cols-3 gap-3">
                <Field label="Item" wide><Input value={belForm.itemDescription} onChange={e=>setBelForm({...belForm,itemDescription:e.target.value})}/></Field>
                <Field label="Quantity"><Input type="number" value={belForm.quantity} onChange={e=>setBelForm({...belForm,quantity:e.target.value})}/></Field>
                <Field label="Condition"><Input value={belForm.condition||''} onChange={e=>setBelForm({...belForm,condition:e.target.value})}/></Field>
              </div>
              <button disabled={saving==='bl'} className="btn-primary text-sm" onClick={()=>run('bl',()=>api.addMortuaryBelonging(body.id,belForm))}>Add item</button>
              <div className="text-sm space-y-1">
                {(body.belongings||[]).map((x:any)=>(
                  <p key={x.id} className="border rounded-lg px-3 py-1.5 bg-slate-50">
                    {x.item_description} × {x.quantity} {x.returned_to?`· returned to ${x.returned_to}`:''}
                    {!x.returned_to&&<button className="ml-2 text-[#02357A] underline" onClick={()=>run('br',()=>api.returnMortuaryBelonging(x.id,{returnedTo:'Family',returnedAt:new Date().toISOString()}))}>Return</button>}
                  </p>
                ))}
              </div>
            </div>
            <div className="card p-5 space-y-3">
              <h3 className="font-semibold text-[#02357A]">Billing & civil registry</h3>
              <p className="text-xs text-slate-500">Charges post into existing HMIS billing. Unpaid bills do not block MLC unless configured.</p>
              <div className="flex flex-wrap gap-2">
                <button disabled={saving==='ch'} className="btn-primary text-sm" onClick={()=>run('ch',()=>api.raiseMortuaryCharges(body.id))}>Raise charges</button>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <Field label="Civil registry number"><Input value={civilNo} onChange={e=>setCivilNo(e.target.value)}/></Field>
                <div className="flex items-end"><button disabled={saving==='cv'} className="btn-secondary text-sm" onClick={()=>run('cv',()=>api.notifyMortuaryCivilRegistry(body.id,{number:civilNo}))}>Notify civil</button></div>
              </div>
              <BodyBilling id={body.id} stamp={`${body.bill_id||''}:${body.updated_at||''}:${saving}`}/>
            </div>
            <div className="card p-5 space-y-3">
              <h3 className="font-semibold text-[#02357A]">Transfer</h3>
              <div className="grid md:grid-cols-2 gap-3">
                <Field label="Transfer type">
                  <Select value={trForm.transferType} onChange={e=>setTrForm({...trForm,transferType:e.target.value})}>
                    <option>CHAMBER</option><option>FACILITY</option><option>POLICE</option><option>FUNERAL_HOME</option><option>OTHER</option>
                  </Select>
                </Field>
                <Field label="Destination"><Input value={trForm.destination||''} onChange={e=>setTrForm({...trForm,destination:e.target.value})}/></Field>
                <Field label="Reason"><Input value={trForm.reason||''} onChange={e=>setTrForm({...trForm,reason:e.target.value})}/></Field>
                <Field label="Authorized person"><Input value={trForm.authorizedPerson||''} onChange={e=>setTrForm({...trForm,authorizedPerson:e.target.value})}/></Field>
              </div>
              {['FACILITY','FUNERAL_HOME','OTHER'].includes(trForm.transferType)&&(
                <>
                  <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={!!trForm.asFinalDisposition} onChange={e=>setTrForm({...trForm,asFinalDisposition:e.target.checked})}/> This is the final disposition (not burial/cremation)</label>
                  {(body.legal_hold||trForm.asFinalDisposition)&&<Field label="Override reason" wide><Input placeholder="Required if legal hold is active" value={trForm.overrideReason||''} onChange={e=>setTrForm({...trForm,overrideReason:e.target.value})}/></Field>}
                </>
              )}
              <button disabled={released||saving==='tr'} className="btn-primary text-sm" onClick={()=>run('tr',()=>api.transferMortuaryBody(body.id,{...trForm,asFinalDisposition:['FACILITY','FUNERAL_HOME','OTHER'].includes(trForm.transferType)&&!!trForm.asFinalDisposition}))}>Transfer</button>
              <div className="text-xs space-y-1">{(body.transfers||[]).map((t:any)=><p key={t.id}>{fmtDateTime(t.transferred_at)} {t.destination||t.to_label} · {t.reason}</p>)}</div>
            </div>
          </div>
        )}

        {tab==='release'&&(
          <div className="grid lg:grid-cols-2 gap-4">
            <div className="card p-5 space-y-3">
              <h3 className="font-semibold text-[#02357A]">Release authorization</h3>
              {gate&&(
                <div className="rounded-xl border bg-slate-50 p-3 space-y-1.5">
                  {(gate.items||[]).map((i:any)=>(
                    <p key={i.key} className={`text-sm ${i.ok?'text-emerald-700':'text-red-700'}`}>{i.ok?'✓':'✗'} {i.label}{i.required?' *':''} <span className="text-slate-500">— {i.detail}</span></p>
                  ))}
                  {(gate.alreadyClosed||released)
                    ? <p className="text-xs text-slate-600 pt-1">This case is already closed. Open billing on Prepare or the full case file.</p>
                    : !gate.canRelease
                      ? <p className="text-xs text-amber-800 pt-1">Blocked: {(gate.blocking||[]).join('; ')||'checklist incomplete'}</p>
                      : <p className="text-xs text-emerald-700 pt-1">Ready to release.</p>}
                </div>
              )}
              <div className="grid md:grid-cols-2 gap-3">
                <Field label="Release type">
                  <Select value={relForm.releaseType} onChange={e=>setRelForm({...relForm,releaseType:e.target.value})}>
                    {opts('RELEASE_TYPE').map((x:any)=><option key={x.code} value={x.code}>{x.label}</option>)}
                  </Select>
                </Field>
                <Field label="Recipient name" required><Input value={relForm.recipientName||''} onChange={e=>setRelForm({...relForm,recipientName:e.target.value})}/></Field>
                <Field label="Relationship"><Input value={relForm.recipientRelationship||''} onChange={e=>setRelForm({...relForm,recipientRelationship:e.target.value})}/></Field>
                <Field label="Recipient ID"><Input value={relForm.recipientIdNumber||''} onChange={e=>setRelForm({...relForm,recipientIdNumber:e.target.value})}/></Field>
                <Field label="Phone"><Input value={relForm.recipientPhone||''} onChange={e=>setRelForm({...relForm,recipientPhone:e.target.value})}/></Field>
                <Field label="Funeral home / organization"><Input value={relForm.organization||''} onChange={e=>setRelForm({...relForm,organization:e.target.value})}/></Field>
                <Field label="Vehicle registration"><Input value={relForm.vehicleRegistration||''} onChange={e=>setRelForm({...relForm,vehicleRegistration:e.target.value})}/></Field>
                <Field label="Destination"><Input value={relForm.destination||''} onChange={e=>setRelForm({...relForm,destination:e.target.value})}/></Field>
                <Field label="Override reason" wide><Input placeholder="Required if a checklist item is blocked" value={relForm.overrideReason||''} onChange={e=>setRelForm({...relForm,overrideReason:e.target.value})}/></Field>
                <Field label="Acknowledgement / signature" wide><Textarea rows={2} value={relForm.acknowledgementSignature||''} onChange={e=>setRelForm({...relForm,acknowledgementSignature:e.target.value})}/></Field>
              </div>
              {body.legal_hold&&<p className="text-xs text-red-700">Legal hold is active. An override reason is required.</p>}
              <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={!!relForm.confirmed} onChange={e=>setRelForm({...relForm,confirmed:e.target.checked})}/> I confirm this release and recipient identity</label>
              <button disabled={released||saving==='rel'} className="btn-primary text-sm" onClick={()=>{
                if(!window.confirm('Confirm final body release? Chamber will be vacated.')) return;
                return run('rel',()=>api.releaseMortuaryBody(body.id,relForm));
              }}>Release body</button>
            </div>
            <div className="card p-5 space-y-3">
              <h3 className="font-semibold text-[#02357A]">Unclaimed / authorized disposition</h3>
              <p className="text-xs text-slate-500">Use burial, cremation, or transfer on Chapel & Disposition for the exclusive final path. This block is for unclaimed follow-up.</p>
              <Field label="Police reference"><Input value={ucForm.policeReference||''} onChange={e=>setUcForm({...ucForm,policeReference:e.target.value,policeNotified:true})}/></Field>
              <Field label="Authority reference"><Input value={ucForm.authorityReference||''} onChange={e=>setUcForm({...ucForm,authorityReference:e.target.value,authorityNotified:true})}/></Field>
              <Field label="Follow-up notes"><Textarea rows={3} value={ucForm.followUpHistory||''} onChange={e=>setUcForm({...ucForm,followUpHistory:e.target.value})}/></Field>
              <button disabled={released||saving==='uc'} className="btn-secondary text-sm" onClick={()=>run('uc',()=>api.updateMortuaryUnclaimed(body.id,ucForm))}>Record unclaimed follow-up</button>
              <Field label="Disposition type"><Input value={ucForm.dispositionType||''} onChange={e=>setUcForm({...ucForm,dispositionType:e.target.value})}/></Field>
              <Field label="Authorized officer"><Input value={ucForm.authorizedOfficer||''} onChange={e=>setUcForm({...ucForm,authorizedOfficer:e.target.value})}/></Field>
              <button disabled={released||saving==='dp'} className="btn-primary text-sm" onClick={()=>{
                if(!window.confirm('Final disposition is irreversible. Continue?')) return;
                return run('dp',()=>api.disposeMortuaryBody(body.id,ucForm));
              }}>Authorized final disposition</button>
            </div>
          </div>
        )}

        {tab==='audit'&&(
          <div className="card p-5">
            <h3 className="font-semibold text-[#02357A] mb-3">Audit trail</h3>
            <div className="space-y-1.5 max-h-[70vh] overflow-auto text-sm">
              {(body.audit||[]).map((a:any)=>(
                <p key={a.id} className="border-b border-slate-100 pb-1.5">{fmtDateTime(a.created_at)} · <span className="font-medium">{a.action}</span> · {a.entity} · {a.first_name||a.email||'system'} {a.reason?`· ${a.reason}`:''}</p>
              ))}
              {!(body.audit||[]).length&&<p className="text-slate-400">No audit rows yet.</p>}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

function BodyBilling({id,stamp}:{id:string;stamp?:string}){
  const [bill,setBill]=useState<any>(null);
  useEffect(()=>{api.getMortuaryBodyBilling(id).then(r=>setBill(unwrap(r))).catch(()=>null);},[id,stamp]);
  if(!bill) return <p className="text-xs text-slate-400">Loading billing…</p>;
  const inv=bill.invoice;
  const events=bill.events||[];
  if(!events.length&&!inv) return <p className="text-xs text-slate-400">No billing events yet. Click Raise charges to post storage, release and preparation fees.</p>;
  return(
    <div className="text-sm space-y-1 pt-1">
      {events.map((e:any)=><p key={e.id} className="text-slate-600">{e.description} · {e.amount}{e.bill_id?'':' · not on HMIS bill yet'}</p>)}
      {inv
        ? <p className="font-semibold text-[#02357A]">{inv.bill_number} · {prettyMortuary(inv.status)} · outstanding {inv.balance_amount}</p>
        : <p className="text-xs text-amber-800">Fees are on the case, but the HMIS bill was not created because this body had no linked patient. Click Raise charges to post them now.</p>}
    </div>
  );
}
